<template>
	<div id="help-center">
		<v-tooltip bottom>
			<template v-slot:activator="{ on, attrs }">
				<v-btn class="mx-1" fab small elevation="0" height="30" width="30" v-bind="attrs" v-on="on" @click="open">
					<v-icon color="#333">mdi-help-circle-outline</v-icon>
				</v-btn>
			</template>
			<span>راهنما</span>
		</v-tooltip>

		<v-dialog v-model="dialog" content-class="help-dialog" max-width="820" transition="dialog-transition">
			<v-card class="help-card">
				<!-- Header -->
				<div class="help-hero">
					<div class="help-hero__badge">
						<v-icon color="white" size="24">mdi-lifebuoy</v-icon>
					</div>
					<div class="help-hero__meta">
						<div class="help-hero__title">{{ activeVideo ? activeVideo.title : 'مرکز راهنما' }}</div>
						<div class="help-hero__desc">{{ activeVideo ? 'پخش ویدیوی آموزشی' : 'آموزش‌ها و پاسخ پرسش‌های پرتکرار سیستم' }}</div>
					</div>
					<v-spacer />
					<v-btn icon @click="dialog = false">
						<v-icon color="white">mdi-close</v-icon>
					</v-btn>
				</div>

				<!-- Search -->
				<div v-if="!activeVideo" class="help-toolbar">
					<v-text-field v-model="search" class="help-search" clearable dense hide-details outlined placeholder="جستجو در راهنماها..." prepend-inner-icon="mdi-magnify" color="primary" />
				</div>

				<!-- Body -->
				<div class="help-body">
					<!-- Inline video player -->
					<div v-if="activeVideo" class="help-player">
						<div class="help-player__head">
							<v-btn icon small @click="activeVideo = null">
								<v-icon>mdi-arrow-right</v-icon>
							</v-btn>
							<span class="help-player__title">{{ activeVideo.title }}</span>
							<v-spacer />
							<v-chip x-small label color="primary" class="help-player__badge">
								<v-icon left x-small>mdi-play</v-icon>
								ویدیو
							</v-chip>
						</div>

						<div class="help-player__frame">
							<video v-if="videoType(activeVideo.url) === 'file'" :src="activeVideo.url" class="help-video" controls autoplay playsinline />
							<iframe v-else :src="embedUrl(activeVideo.url)" class="help-iframe" allow="autoplay; fullscreen; encrypted-media; picture-in-picture" allowfullscreen />
						</div>

						<div class="help-player__foot">
							<v-btn text small color="primary" @click="activeVideo = null">
								<v-icon right small>mdi-arrow-right</v-icon>
								بازگشت به فهرست راهنماها
							</v-btn>
						</div>
					</div>

					<template v-else>
						<div v-if="loading" class="help-state">
							<v-progress-circular color="primary" indeterminate size="44" width="3" />
							<div class="help-state__text">در حال بارگذاری راهنماها...</div>
						</div>

						<div v-else-if="!groups.length" class="help-state">
							<v-icon color="grey lighten-2" size="72">mdi-book-search-outline</v-icon>
							<div class="help-state__text">{{ error || 'راهنمایی برای نمایش پیدا نشد.' }}</div>
						</div>

						<template v-else>
							<div v-for="group in groups" :key="group.category" class="help-group">
								<div class="help-group__head">
									<span class="help-group__dot" />
									<span class="help-group__title">{{ group.category }}</span>
									<span class="help-group__count">{{ group.items.length }}</span>
								</div>

								<div class="help-grid">
									<div
										v-for="(item, index) in group.items"
										:key="index"
										class="help-tile"
										:class="{ 'help-tile--disabled': !item.url, 'help-tile--video': !!videoType(item.url) }"
										@click="onTileClick(item)"
									>
										<div class="help-tile__icon">
											<v-icon small>{{ videoType(item.url) ? 'mdi-play-circle-outline' : 'mdi-file-document-outline' }}</v-icon>
										</div>
										<div class="help-tile__title">{{ item.title }}</div>
										<v-icon v-if="videoType(item.url)" class="help-tile__arrow" small>mdi-play</v-icon>
										<v-icon v-else-if="item.url" class="help-tile__arrow" small>mdi-open-in-new</v-icon>
										<span v-else class="help-tile__soon">به زودی</span>
									</div>
								</div>
							</div>
						</template>
					</template>
				</div>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
	import helpService from '@/services/help.service'
	import toast from '@/util/toast'

	export default {
		name: 'HelpCenter',

		data() {
			return {
				dialog: false,
				loading: false,
				loaded: false,
				error: '',
				helps: [],
				search: '',
				activeVideo: null
			}
		},

		computed: {
			adminHelps() {
				return this.helps.filter(help => helpService.isAdminPanel(help.panel))
			},
			filteredHelps() {
				const query = (this.search || '').trim().toLowerCase()
				if (!query)
					return this.adminHelps

				return this.adminHelps.filter(help => {
					const title = (help.title || '').toLowerCase()
					const category = (help.category || '').toLowerCase()
					return title.includes(query) || category.includes(query)
				})
			},
			groups() {
				const grouped = {}
				const order = []

				this.filteredHelps.forEach(help => {
					const category = help.category || 'سایر'
					if (!grouped[category]) {
						grouped[category] = []
						order.push(category)
					}
					grouped[category].push(help)
				})

				return order.map(category => ({ category, items: grouped[category] }))
			}
		},

		watch: {
			dialog(value) {
				// Stop inline playback as soon as the dialog is closed.
				if (!value)
					this.activeVideo = null
			}
		},

		methods: {
			open() {
				this.dialog = true
				if (!this.loaded)
					this.load()
			},
			load() {
				this.loading = true
				this.error = ''
				helpService.getHelps()
					.then(response => {
						this.helps = Array.isArray(response) ? response : []
						this.loaded = true
					})
					.catch(err => {
						this.error = err?.message || 'خطا در دریافت راهنماها'
						toast.error(this.error)
					})
					.finally(() => {
						this.loading = false
					})
			},
			onTileClick(item) {
				if (!item.url)
					return

				if (this.videoType(item.url))
					this.activeVideo = item
				else
					window.open(item.url, '_blank', 'noopener,noreferrer')
			},
			// 'file' for direct video files, or the hosting provider name.
			videoType(url) {
				if (!url)
					return null

				const clean = String(url).split('?')[0].split('#')[0].toLowerCase()

				if (/\.(mp4|webm|ogg|ogv|mov|m4v|m3u8)$/.test(clean))
					return 'file'
				if (clean.includes('aparat.com'))
					return 'aparat'
				if (clean.includes('youtube.com') || clean.includes('youtu.be'))
					return 'youtube'
				if (clean.includes('vimeo.com'))
					return 'vimeo'

				return null
			},
			embedUrl(url) {
				const type = this.videoType(url)

				if (type === 'aparat') {
					const match = url.match(/aparat\.com\/v\/([A-Za-z0-9]+)/)
					return match ? `https://www.aparat.com/video/video/embed/videohash/${match[1]}/vt/frame` : url
				}

				if (type === 'youtube') {
					const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]+)/)
					return match ? `https://www.youtube.com/embed/${match[1]}` : url
				}

				if (type === 'vimeo') {
					const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/)
					return match ? `https://player.vimeo.com/video/${match[1]}` : url
				}

				return url
			}
		}
	}
</script>

<style scoped>
	.help-card {
		border-radius: 18px;
		overflow: hidden;
		background: #f7f8fa;
	}

	.help-hero {
		display: flex;
		align-items: center;
		gap: 14px;
		padding: 20px 22px;
		background: linear-gradient(135deg, #E91E63 0%, #9C27B0 100%);
	}

	.help-hero__badge {
		display: flex;
		align-items: center;
		justify-content: center;
		flex: 0 0 auto;
		width: 46px;
		height: 46px;
		border-radius: 14px;
		background: rgba(255, 255, 255, .18);
	}

	.help-hero__meta {
		min-width: 0;
		line-height: 1.6;
	}

	.help-hero__title {
		color: #fff;
		font-size: 1.15rem;
		font-weight: 700;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.help-hero__desc {
		color: rgba(255, 255, 255, .82);
		font-size: .8rem;
	}

	.help-toolbar {
		padding: 16px 22px 4px;
	}

	.help-search>>>.v-input__slot {
		background: #fff !important;
		border-radius: 12px;
	}

	.help-body {
		max-height: 58vh;
		overflow-y: auto;
		padding: 12px 22px 22px;
	}

	.help-group {
		margin-bottom: 20px;
	}

	.help-group:last-child {
		margin-bottom: 4px;
	}

	.help-group__head {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 10px;
	}

	.help-group__dot {
		width: 8px;
		height: 8px;
		border-radius: 50%;
		background: #E91E63;
	}

	.help-group__title {
		font-size: .95rem;
		font-weight: 700;
		color: #2b2b2b;
	}

	.help-group__count {
		min-width: 22px;
		padding: 1px 7px;
		border-radius: 999px;
		font-size: .72rem;
		text-align: center;
		color: #9C27B0;
		background: rgba(156, 39, 176, .1);
	}

	.help-grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
		gap: 10px;
	}

	.help-tile {
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 12px 14px;
		border: 1px solid rgba(0, 0, 0, .07);
		border-radius: 14px;
		background: #fff;
		text-decoration: none;
		cursor: pointer;
		transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
	}

	.help-tile:hover {
		transform: translateY(-2px);
		border-color: rgba(233, 30, 99, .45);
		box-shadow: 0 8px 20px rgba(233, 30, 99, .12);
	}

	.help-tile__icon {
		display: flex;
		align-items: center;
		justify-content: center;
		flex: 0 0 auto;
		width: 34px;
		height: 34px;
		border-radius: 10px;
		color: #E91E63;
		background: rgba(233, 30, 99, .1);
	}

	.help-tile--video .help-tile__icon {
		color: #9C27B0;
		background: rgba(156, 39, 176, .12);
	}

	.help-tile__title {
		flex: 1 1 auto;
		font-size: .88rem;
		font-weight: 600;
		color: #333;
	}

	.help-tile__arrow {
		color: #bdbdbd;
	}

	.help-tile--video .help-tile__arrow {
		color: #9C27B0;
	}

	.help-tile--disabled {
		cursor: default;
		opacity: .65;
	}

	.help-tile--disabled:hover {
		transform: none;
		border-color: rgba(0, 0, 0, .07);
		box-shadow: none;
	}

	.help-tile__soon {
		flex: 0 0 auto;
		padding: 2px 8px;
		border-radius: 999px;
		font-size: .68rem;
		color: #9e9e9e;
		background: #f0f0f0;
	}

	.help-player {
		padding-top: 6px;
	}

	.help-player__head {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 12px;
	}

	.help-player__title {
		font-size: .95rem;
		font-weight: 700;
		color: #2b2b2b;
	}

	.help-player__badge {
		flex: 0 0 auto;
	}

	.help-player__frame {
		position: relative;
		width: 100%;
		padding-top: 56.25%;
		border-radius: 16px;
		overflow: hidden;
		background: #000;
		box-shadow: 0 12px 30px rgba(0, 0, 0, .18);
	}

	.help-video,
	.help-iframe {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		border: 0;
	}

	.help-player__foot {
		display: flex;
		justify-content: center;
		margin-top: 8px;
	}

	.help-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 54px 0;
		text-align: center;
	}

	.help-state__text {
		margin-top: 14px;
		color: #9e9e9e;
		font-size: .88rem;
	}
</style>
